import { Suspense, lazy } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import {
  getExercise,
  getExercisePersonalRecords,
  getExerciseProgression,
} from "../api-client";
import { ChartFallback } from "../components/ChartFallback";
import { PersonalRecordBadges } from "../components/PersonalRecordBadges";

const ProgressionChart = lazy(() =>
  import("../components/ProgressionChart").then((m) => ({
    default: m.ProgressionChart,
  })),
);

export function ExerciseHistory() {
  const { id } = useParams<{ id: string }>();
  const exerciseId = id ? Number(id) : Number.NaN;
  const valid = Number.isInteger(exerciseId) && exerciseId >= 1;

  const exerciseQuery = useQuery({
    queryKey: ["exercise", exerciseId],
    queryFn: () => getExercise(exerciseId),
    enabled: valid,
  });

  const seriesQuery = useQuery({
    queryKey: ["progression", exerciseId],
    queryFn: () => getExerciseProgression(exerciseId),
    enabled: valid,
  });

  const prsQuery = useQuery({
    queryKey: ["personal-records", exerciseId],
    queryFn: () => getExercisePersonalRecords(exerciseId),
    enabled: valid,
  });

  if (!valid) {
    return (
      <main className="min-h-dvh p-6">
        <p className="text-sm text-red-400" role="alert">
          Invalid exercise id.
        </p>
      </main>
    );
  }

  return (
    <main className="min-h-dvh p-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <Link
            to="/exercises"
            className="text-xs text-slate-400 hover:text-slate-200"
          >
            ← Exercises
          </Link>
          <h1 className="mt-1 text-2xl font-semibold">
            {exerciseQuery.data?.name ?? "…"}
          </h1>
          {exerciseQuery.data?.muscle_group && (
            <p className="text-xs text-slate-400">
              {exerciseQuery.data.muscle_group}
            </p>
          )}
        </div>
      </header>

      {seriesQuery.data && seriesQuery.data.length > 0 && (
        <section
          aria-label="progression summary"
          className="grid grid-cols-3 gap-2"
        >
          <SummaryStat
            label="Sessions"
            value={String(seriesQuery.data.length)}
          />
          <SummaryStat
            label="Latest e1RM"
            value={`${formatKg(
              seriesQuery.data[seriesQuery.data.length - 1]!.best_set.e1rm,
            )} kg`}
          />
          <SummaryStat
            label="Best e1RM"
            value={`${formatKg(
              seriesQuery.data.reduce(
                (m, p) => Math.max(m, p.best_set.e1rm),
                0,
              ),
            )} kg`}
          />
        </section>
      )}

      <section aria-label="personal records" className="space-y-2">
        <h2 className="text-sm font-medium text-slate-300">Personal records</h2>
        {prsQuery.isError && (
          <p className="text-sm text-red-400" role="alert">
            Failed to load personal records.
          </p>
        )}
        {prsQuery.data && <PersonalRecordBadges records={prsQuery.data} />}
      </section>

      <section
        className="rounded-lg border border-slate-800 bg-slate-900 p-4 space-y-3"
        aria-label="progression chart"
      >
        <h2 className="text-sm font-medium text-slate-300">
          Estimated 1RM over time
        </h2>
        {seriesQuery.isError && (
          <p className="text-sm text-red-400" role="alert">
            Failed to load progression.
          </p>
        )}
        {seriesQuery.isPending && (
          <p className="text-sm text-slate-400">Loading…</p>
        )}
        {seriesQuery.data && (
          <Suspense fallback={<ChartFallback />}>
            <ProgressionChart points={seriesQuery.data} />
          </Suspense>
        )}
      </section>
    </main>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function formatKg(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}
